import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FolderPlus, Search, History, FileDown, Archive, Package, ScanLine, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { eurOr, useWh, WH_NAMES } from "@/lib/wh";
import { WhSwitch } from "@/components/wh/WhSwitch";
import { BulkBar } from "@/components/wh/BulkBar";
import { StatsGrid } from "@/components/wh/StatsGrid";
import { LotDialog } from "@/components/wh/LotDialog";
import { PdfDialog } from "@/components/wh/PdfDialog";
import { ScanDialog } from "@/components/wh/ScanDialog";

function LotCard({ lot, picked, onPick }) {
  return (
    <div className="relative">
    <input type="checkbox" checked={picked} onChange={() => onPick(lot.id)} className="absolute left-3 top-3 z-10 h-5 w-5" aria-label={`Seleziona ${lot.name}`} data-testid={`wh-lot-check-${lot.id}`} />
    <Link to={`/magazzino/lotti/${lot.id}`} className="card-lift block rounded-2xl bg-white border border-[var(--border)] overflow-hidden" data-testid={`wh-lot-card-${lot.id}`}>
      {lot.photo_path ? <img src={fileUrl(lot.photo_path)} alt="" className="h-32 w-full object-cover" loading="lazy" />
        : <div className="h-32 bg-[var(--surface-2)] grid place-items-center"><Package className="h-8 w-8 text-[var(--muted)]" /></div>}
      <div className="p-4">
        <p className="font-heading text-lg font-semibold truncate">{lot.name}</p>
        <p className="text-xs text-[var(--muted)]">{lot.stats.items} articoli · {lot.stats.available} disponibili · {lot.stats.sold} venduti</p>
        <p className="text-sm font-mono mt-2">Giacenze {eurOr(lot.stats.potential)}</p>
      </div>
    </Link>
    </div>
  );
}

export default function Warehouse() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lots, setLots] = useState(null);
  const [archived, setArchived] = useState(false);
  const [lotOpen, setLotOpen] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [scan, setScan] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const { wh } = useWh();
  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/wh/stats", { params: { wh } }), api.get("/wh/lots", { params: { archived, wh } })]);
      setStats(s.data);
      setLots(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [archived, wh]);
  useEffect(() => { setPicked(new Set()); }, [archived, wh]);
  const pick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);
  const act = "btn-secondary h-11 px-4 text-sm";
  return (
    <div className="space-y-8">
      <div className="fade-up"><p className="eyebrow">Giacenze</p><h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="wh-title">{WH_NAMES[wh]}</h1></div>
      <WhSwitch />
      <div className="flex flex-wrap gap-2" data-testid="wh-actions">
        {can(user, "wh_write") && <Link to="/magazzino/articoli/nuovo" className="btn-primary h-11 px-5" data-testid="wh-new-item"><Plus className="h-5 w-5" /> Nuovo articolo</Link>}
        {can(user, "wh_manage") && <button onClick={() => setLotOpen(true)} className={act} data-testid="wh-new-lot"><FolderPlus className="h-4 w-4" /> Nuovo lotto</button>}
        {can(user, "wh_write") && <Link to="/magazzino/raffica" className={act} data-testid="wh-quick"><Zap className="h-4 w-4" /> Carica a raffica</Link>}
        <button onClick={() => setScan(true)} className={act} data-testid="wh-scan"><ScanLine className="h-4 w-4" /> Scansiona</button>
        <Link to="/magazzino/cerca" className={act} data-testid="wh-global-search"><Search className="h-4 w-4" /> Ricerca globale</Link>
        <Link to="/magazzino/movimenti" className={act} data-testid="wh-history"><History className="h-4 w-4" /> Storico movimenti</Link>
        <button onClick={() => setPdf(true)} className={act} data-testid="wh-inventory"><FileDown className="h-4 w-4" /> Inventario ed esportazioni</button>
      </div>
      <section className="space-y-3"><h2 className="font-heading text-lg font-semibold">Statistiche economiche</h2><StatsGrid stats={stats} /></section>
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-semibold">{archived ? "Lotti archiviati" : "Gestione lotti"}</h2>
          <button onClick={() => setArchived(!archived)} className="btn-ghost text-sm flex items-center gap-1" data-testid="wh-toggle-archived"><Archive className="h-4 w-4" /> {archived ? "Lotti attivi" : "Archiviati"}</button>
        </div>
        <BulkBar ids={[...picked]} lots={(lots || []).filter((l) => !picked.has(l.id))} endpoint="/wh/lots/bulk" kind="lots" canMove={!archived} onDone={() => { setPicked(new Set()); load(); }} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wh-lots">
          {(lots || []).map((l) => <LotCard key={l.id} lot={l} picked={picked.has(l.id)} onPick={pick} />)}
          {!archived && <Link to="/magazzino/cerca?lot=none" className="rounded-2xl border border-dashed p-6 grid place-items-center text-sm text-[var(--muted)]" data-testid="wh-no-lot">Da smistare (senza lotto)</Link>}
        </div>
        {lots && !lots.length && <p className="text-sm text-[var(--muted)]" data-testid="wh-lots-empty">{archived ? "Nessun lotto archiviato" : "Nessun lotto: creane uno con \"Nuovo lotto\"."}</p>}
      </section>
      <LotDialog open={lotOpen} onOpenChange={setLotOpen} onSaved={load} wh={wh} />
      <PdfDialog open={pdf} onOpenChange={setPdf} title={WH_NAMES[wh]} wh={wh} />
      <ScanDialog open={scan} onOpenChange={setScan} />
    </div>
  );
}
